import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FlaskConical, Plus, Play, Pause, Trophy, BarChart3, Loader2, RefreshCw,
  ArrowUpRight, ArrowDownRight, Eye, MousePointer, Target, Trash2, Edit2,
  CheckCircle, XCircle, Clock, TrendingUp, Sparkles, Zap, Settings2, Link2, Megaphone
} from 'lucide-react';
import api, { abTestingAPI } from '../api';

const CONTENT_TYPES = [
  { value: 'headline', label: 'Headlines' },
  { value: 'tagline', label: 'Taglines' },
  { value: 'cta', label: 'Call-to-Actions' },
  { value: 'body_copy', label: 'Body Copy' },
  { value: 'full_copy', label: 'Full Copy' },
  { value: 'email_subject', label: 'Email Subject' },
  { value: 'ad_copy', label: 'Ad Copy' }
];

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: Edit2 },
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: Play },
  paused: { label: 'Paused', color: 'bg-amber-100 text-amber-700', icon: Pause },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

export const ABTestingPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tests, setTests] = useState([]);
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [saving, setSaving] = useState(false);
  const [smartSchedule, setSmartSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [optimizing, setOptimizing] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const [newTest, setNewTest] = useState({
    name: '',
    description: '',
    content_type: 'headline',
    campaign_id: '',
    variations: [
      { content: '', label: 'Control', is_control: true },
      { content: '', label: 'Variation B', is_control: false }
    ],
    target_metric: 'conversion_rate',
    minimum_sample_size: 100,
    auto_optimize: false,
    optimization_threshold: 90,
    min_impressions_before_optimize: 50
  });

  const [editTest, setEditTest] = useState({
    name: '',
    description: '',
    content_type: 'headline',
    variations: [],
    target_metric: 'conversion_rate',
    minimum_sample_size: 100,
    auto_optimize: false,
    optimization_threshold: 90,
    min_impressions_before_optimize: 50
  });

  const [metricsForm, setMetricsForm] = useState({
    variation_id: '',
    impressions: 0,
    clicks: 0,
    conversions: 0,
    revenue: 0
  });

  useEffect(() => {
    fetchData();
    fetchCampaigns();
  }, [activeTab]);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/marketing/campaigns', { params: { limit: 100 } });
      setCampaigns(res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const params = activeTab !== 'all' ? { status: activeTab } : {};
      const [testsRes, overviewRes] = await Promise.all([
        abTestingAPI.getAll(params),
        abTestingAPI.getOverview()
      ]);
      setTests(testsRes.data || []);
      setOverview(overviewRes.data);
    } catch (error) {
      console.error('Failed to fetch A/B tests:', error);
      toast.error('Failed to load A/B tests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleCreateTest = async () => {
    if (!newTest.name) {
      toast.error('Test name is required');
      return;
    }
    if (newTest.variations.some(v => !v.content.trim())) {
      toast.error('All variations must have content');
      return;
    }

    try {
      setSaving(true);
      await abTestingAPI.create(newTest);
      toast.success('A/B test created');
      setShowCreateDialog(false);
      resetNewTest();
      fetchData();
    } catch (error) {
      toast.error('Failed to create test');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (testId, newStatus) => {
    try {
      await abTestingAPI.update(testId, { status: newStatus });
      toast.success(`Test ${newStatus === 'active' ? 'started' : newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleConclude = async (testId) => {
    try {
      await abTestingAPI.conclude(testId);
      toast.success('Test concluded - winner determined!');
      fetchData();
    } catch (error) {
      toast.error('Failed to conclude test');
    }
  };

  const handleUpdateMetrics = async () => {
    if (!selectedTest || !metricsForm.variation_id) return;

    try {
      setSaving(true);
      await abTestingAPI.updateMetrics(selectedTest.id, metricsForm);
      toast.success('Metrics updated');
      setShowMetricsDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update metrics');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (testId) => {
    if (!confirm('Delete this A/B test?')) return;
    try {
      await abTestingAPI.delete(testId);
      toast.success('Test deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete test');
    }
  };

  const openMetricsDialog = (test) => {
    setSelectedTest(test);
    setMetricsForm({
      variation_id: test.variations?.[0]?.id || '',
      impressions: 0,
      clicks: 0,
      conversions: 0,
      revenue: 0
    });
    setShowMetricsDialog(true);
  };

  const resetNewTest = () => {
    setNewTest({
      name: '',
      description: '',
      content_type: 'headline',
      campaign_id: '',
      variations: [
        { content: '', label: 'Control', is_control: true },
        { content: '', label: 'Variation B', is_control: false }
      ],
      target_metric: 'conversion_rate',
      minimum_sample_size: 100,
      auto_optimize: false,
      optimization_threshold: 90,
      min_impressions_before_optimize: 50
    });
    setSmartSchedule(null);
  };

  const fetchSmartSchedule = async () => {
    try {
      setLoadingSchedule(true);
      const res = await abTestingAPI.getSmartSchedule({
        content_type: newTest.content_type,
        num_variations: newTest.variations.length,
        target_metric: newTest.target_metric
      });
      setSmartSchedule(res.data);
      
      // Auto-apply recommendations
      if (res.data.recommended_sample_size_per_variation) {
        setNewTest(prev => ({
          ...prev,
          minimum_sample_size: res.data.recommended_sample_size_per_variation
        }));
      }
      
      toast.success('Smart schedule recommendations loaded!');
    } catch (error) {
      console.error('Failed to get smart schedule:', error);
      toast.error('Failed to get recommendations');
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleOptimize = async (testId) => {
    try {
      setOptimizing(testId);
      const res = await abTestingAPI.optimize(testId);
      if (res.data.action_taken) {
        toast.success(`Optimization complete! Current leader: ${res.data.current_leader}`);
      } else {
        toast.info(res.data.reason || 'No optimization needed');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to optimize test');
    } finally {
      setOptimizing(null);
    }
  };

  const handleEditTest = (test) => {
    setSelectedTest(test);
    setEditTest({
      name: test.name || '',
      description: test.description || '',
      content_type: test.content_type || 'headline',
      variations: test.variations?.map(v => ({
        id: v.id,
        content: v.content || '',
        label: v.label || '',
        is_control: v.is_control || false
      })) || [],
      target_metric: test.target_metric || 'conversion_rate',
      minimum_sample_size: test.minimum_sample_size || 100,
      auto_optimize: test.auto_optimize || false,
      optimization_threshold: test.optimization_threshold || 90,
      min_impressions_before_optimize: test.min_impressions_before_optimize || 50
    });
    setShowEditDialog(true);
  };

  const handleUpdateTest = async () => {
    if (!selectedTest) return;
    
    try {
      setSaving(true);
      const updateData = {
        name: editTest.name,
        description: editTest.description,
        target_metric: editTest.target_metric,
        minimum_sample_size: editTest.minimum_sample_size,
        auto_optimize: editTest.auto_optimize,
        optimization_threshold: editTest.optimization_threshold,
        min_impressions_before_optimize: editTest.min_impressions_before_optimize
      };
      
      // Only update variations if test is in draft status
      if (selectedTest.status === 'draft') {
        updateData.variations = editTest.variations;
      }
      
      await abTestingAPI.update(selectedTest.id, updateData);
      toast.success('Test updated successfully');
      setShowEditDialog(false);
      setSelectedTest(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update test');
    } finally {
      setSaving(false);
    }
  };

  const addEditVariation = () => {
    const nextLetter = String.fromCharCode(65 + editTest.variations.length);
    setEditTest({
      ...editTest,
      variations: [
        ...editTest.variations,
        { content: '', label: `Variation ${nextLetter}`, is_control: false }
      ]
    });
  };

  const removeEditVariation = (index) => {
    if (editTest.variations.length <= 2) {
      toast.error('Minimum 2 variations required');
      return;
    }
    setEditTest({
      ...editTest,
      variations: editTest.variations.filter((_, i) => i !== index)
    });
  };

  const updateEditVariation = (index, field, value) => {
    const updated = [...editTest.variations];
    updated[index] = { ...updated[index], [field]: value };
    setEditTest({ ...editTest, variations: updated });
  };

  const addVariation = () => {
    const nextLetter = String.fromCharCode(65 + newTest.variations.length);
    setNewTest({
      ...newTest,
      variations: [
        ...newTest.variations,
        { content: '', label: `Variation ${nextLetter}`, is_control: false }
      ]
    });
  };

  const removeVariation = (index) => {
    if (newTest.variations.length <= 2) {
      toast.error('Minimum 2 variations required');
      return;
    }
    setNewTest({
      ...newTest,
      variations: newTest.variations.filter((_, i) => i !== index)
    });
  };

  const updateVariation = (index, field, value) => {
    const updated = [...newTest.variations];
    updated[index] = { ...updated[index], [field]: value };
    setNewTest({ ...newTest, variations: updated });
  };

  const getWinnerVariation = (test) => {
    if (!test.winning_variation_id) return null;
    return test.variations?.find(v => v.id === test.winning_variation_id);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ab-testing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="h-6 w-6" />
            A/B Testing
          </h1>
          <p className="text-muted-foreground">
            Test and optimize your AI-generated content variations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={fetchData} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {hasRole(['admin', 'marketing_manager']) && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-ab-test-btn">
              <Plus className="h-4 w-4 mr-2" />
              New A/B Test
            </Button>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <FlaskConical className="h-8 w-8 text-purple-500" />
            </div>
            <p className="text-2xl font-bold">{overview?.total_tests || 0}</p>
            <p className="text-xs text-muted-foreground">Total Tests</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <Play className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-2xl font-bold">{overview?.active_tests || 0}</p>
            <p className="text-xs text-muted-foreground">Active Tests</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-8 w-8 text-blue-500" />
            </div>
            <p className="text-2xl font-bold">{overview?.completed_tests || 0}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-8 w-8 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-emerald-600">+{overview?.avg_lift_percent || 0}%</p>
            <p className="text-xs text-muted-foreground">Avg. Lift</p>
          </CardContent>
        </Card>
      </div>

      {/* Tests List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>A/B Tests</CardTitle>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
                <TabsTrigger value="draft">Drafts</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {tests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FlaskConical className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No A/B tests found</p>
              <p className="text-sm mt-2">Create your first test to start optimizing content</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tests.map((test) => {
                const StatusIcon = STATUS_CONFIG[test.status]?.icon || Clock;
                const winner = getWinnerVariation(test);

                return (
                  <div
                    key={test.id}
                    className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`ab-test-${test.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{test.name}</h3>
                          <Badge className={STATUS_CONFIG[test.status]?.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {STATUS_CONFIG[test.status]?.label}
                          </Badge>
                          <Badge variant="outline">
                            {CONTENT_TYPES.find(t => t.value === test.content_type)?.label || test.content_type}
                          </Badge>
                          {test.campaign_id && (
                            <Link to={`/marketing/campaigns/${test.campaign_id}`}>
                              <Badge variant="secondary" className="bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200">
                                <Megaphone className="h-3 w-3 mr-1" />
                                {campaigns.find(c => c.id === test.campaign_id)?.name || 'Linked Campaign'}
                              </Badge>
                            </Link>
                          )}
                          {test.ai_generated && (
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                              <Sparkles className="h-3 w-3 mr-1" />
                              AI Generated
                            </Badge>
                          )}
                          {test.auto_optimize && (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                              <Zap className="h-3 w-3 mr-1" />
                              Auto-Optimize
                            </Badge>
                          )}
                        </div>
                        {test.description && (
                          <p className="text-sm text-muted-foreground mb-2">{test.description}</p>
                        )}

                        {/* Variations Preview */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                          {test.variations?.slice(0, 3).map((variation, idx) => (
                            <div
                              key={variation.id}
                              className={`p-2 border rounded text-xs ${
                                variation.is_paused
                                  ? 'border-slate-300 bg-slate-50 opacity-60'
                                  : winner?.id === variation.id
                                  ? 'border-green-500 bg-green-50'
                                  : variation.is_control
                                  ? 'border-blue-300 bg-blue-50'
                                  : ''
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium">{variation.label}</span>
                                <div className="flex items-center gap-1">
                                  {variation.is_paused && (
                                    <Pause className="h-3 w-3 text-slate-400" />
                                  )}
                                  {winner?.id === variation.id && (
                                    <Trophy className="h-3 w-3 text-amber-500" />
                                  )}
                                </div>
                              </div>
                              <p className="text-muted-foreground line-clamp-2">{variation.content}</p>
                              {test.status !== 'draft' && (
                                <div className="flex items-center gap-2 mt-1 text-xs">
                                  <span><Eye className="h-3 w-3 inline" /> {formatNumber(variation.impressions)}</span>
                                  <span><MousePointer className="h-3 w-3 inline" /> {variation.ctr?.toFixed(1) || 0}%</span>
                                  <span><Target className="h-3 w-3 inline" /> {variation.conversion_rate?.toFixed(1) || 0}%</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Results for completed tests */}
                        {test.status === 'completed' && test.lift_percent !== undefined && (
                          <div className="mt-3 p-2 bg-emerald-50 rounded-lg">
                            <div className="flex items-center gap-4 text-sm">
                              <span className="font-medium text-emerald-700">
                                <TrendingUp className="h-4 w-4 inline mr-1" />
                                {test.lift_percent > 0 ? '+' : ''}{test.lift_percent}% lift
                              </span>
                              <span className="text-muted-foreground">
                                {test.statistical_significance?.toFixed(0)}% confidence
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 ml-4">
                        {/* Edit button - available for all non-completed tests */}
                        {test.status !== 'completed' && hasRole(['admin', 'marketing_manager']) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditTest(test)}
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        )}
                        {test.status === 'draft' && hasRole(['admin', 'marketing_manager']) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateStatus(test.id, 'active')}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Start
                          </Button>
                        )}
                        {test.status === 'active' && hasRole(['admin', 'marketing_manager']) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openMetricsDialog(test)}
                            >
                              <BarChart3 className="h-4 w-4 mr-1" />
                              Metrics
                            </Button>
                            {test.auto_optimize && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOptimize(test.id)}
                                disabled={optimizing === test.id}
                              >
                                {optimizing === test.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Zap className="h-4 w-4 mr-1" />
                                )}
                                Optimize
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpdateStatus(test.id, 'paused')}
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleConclude(test.id)}
                            >
                              <Trophy className="h-4 w-4 mr-1" />
                              Conclude
                            </Button>
                          </>
                        )}
                        {test.status === 'paused' && hasRole(['admin', 'marketing_manager']) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateStatus(test.id, 'active')}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Resume
                          </Button>
                        )}
                        {hasRole(['admin']) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(test.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* By Content Type */}
      {overview?.tests_by_content_type && Object.keys(overview.tests_by_content_type).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tests by Content Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(overview.tests_by_content_type).map(([type, data]) => (
                <div key={type} className="p-3 border rounded-lg">
                  <p className="font-medium capitalize">{type.replace('_', ' ')}</p>
                  <p className="text-2xl font-bold">{data.total}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                    <span>{data.completed} completed</span>
                    {data.avg_lift > 0 && (
                      <span className="text-green-600">+{data.avg_lift}% avg lift</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Test Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-5 w-5" />
              Create A/B Test
            </DialogTitle>
            <DialogDescription>
              Test different content variations to find what performs best
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Test Name *</Label>
                <Input
                  placeholder="e.g., Q1 Campaign Headlines"
                  value={newTest.name}
                  onChange={(e) => setNewTest({ ...newTest, name: e.target.value })}
                  data-testid="test-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select
                  value={newTest.content_type}
                  onValueChange={(v) => setNewTest({ ...newTest, content_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="What are you testing and why?"
                value={newTest.description}
                onChange={(e) => setNewTest({ ...newTest, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Campaign Link */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Link to Campaign (Optional)
              </Label>
              <Select
                value={newTest.campaign_id || 'none'}
                onValueChange={(v) => setNewTest({ ...newTest, campaign_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a campaign..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Campaign</SelectItem>
                  {campaigns.map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      <div className="flex items-center gap-2">
                        <Megaphone className="h-3 w-3" />
                        {campaign.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Linking to a campaign will show test results on the campaign detail page</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Variations *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addVariation}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Variation
                </Button>
              </div>
              {newTest.variations.map((variation, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        value={variation.label}
                        onChange={(e) => updateVariation(idx, 'label', e.target.value)}
                        placeholder="Label"
                        className="w-32"
                      />
                      {variation.is_control && (
                        <Badge variant="secondary">Control</Badge>
                      )}
                    </div>
                    <Textarea
                      value={variation.content}
                      onChange={(e) => updateVariation(idx, 'content', e.target.value)}
                      placeholder={`Enter ${newTest.content_type} content...`}
                      rows={2}
                      data-testid={`variation-${idx}-content`}
                    />
                  </div>
                  {newTest.variations.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeVariation(idx)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Metric</Label>
                <Select
                  value={newTest.target_metric}
                  onValueChange={(v) => setNewTest({ ...newTest, target_metric: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ctr">Click-Through Rate</SelectItem>
                    <SelectItem value="conversion_rate">Conversion Rate</SelectItem>
                    <SelectItem value="revenue">Revenue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Min. Sample Size (per variation)</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={fetchSmartSchedule}
                    disabled={loadingSchedule}
                    className="h-6 text-xs"
                  >
                    {loadingSchedule ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <Sparkles className="h-3 w-3 mr-1" />
                    )}
                    Smart Schedule
                  </Button>
                </div>
                <Input
                  type="number"
                  value={newTest.minimum_sample_size}
                  onChange={(e) => setNewTest({ ...newTest, minimum_sample_size: parseInt(e.target.value) || 100 })}
                  min={10}
                />
              </div>
            </div>

            {/* Smart Schedule Recommendations */}
            {smartSchedule && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  <Label className="text-blue-900">Smart Schedule Recommendations</Label>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Recommended Duration</p>
                    <p className="font-medium">{smartSchedule.recommended_duration_days} days</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Sample Size/Variation</p>
                    <p className="font-medium">{smartSchedule.recommended_sample_size_per_variation?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Best Days to Start</p>
                    <p className="font-medium">{smartSchedule.best_days_to_start?.join(', ')}</p>
                  </div>
                </div>
                {smartSchedule.tips && (
                  <div className="text-xs text-muted-foreground">
                    <p className="font-medium mb-1">Tips:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {smartSchedule.tips.slice(0, 3).map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Auto-Optimization Settings */}
            <div className="p-4 bg-amber-50 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-600" />
                  <div>
                    <Label>Auto-Optimization</Label>
                    <p className="text-xs text-muted-foreground">Automatically pause underperformers and shift traffic to winners</p>
                  </div>
                </div>
                <Switch
                  checked={newTest.auto_optimize}
                  onCheckedChange={(v) => setNewTest({ ...newTest, auto_optimize: v })}
                />
              </div>

              {newTest.auto_optimize && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-amber-200">
                  <div className="space-y-2">
                    <Label className="text-xs">Confidence Threshold (%)</Label>
                    <Input
                      type="number"
                      value={newTest.optimization_threshold}
                      onChange={(e) => setNewTest({ ...newTest, optimization_threshold: parseInt(e.target.value) || 90 })}
                      min={50}
                      max={99}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Min Impressions Before Optimize</Label>
                    <Input
                      type="number"
                      value={newTest.min_impressions_before_optimize}
                      onChange={(e) => setNewTest({ ...newTest, min_impressions_before_optimize: parseInt(e.target.value) || 50 })}
                      min={10}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTest} disabled={saving} data-testid="save-test-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Metrics Dialog */}
      <Dialog open={showMetricsDialog} onOpenChange={setShowMetricsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Variation Metrics</DialogTitle>
            <DialogDescription>{selectedTest?.name}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Variation</Label>
              <Select
                value={metricsForm.variation_id}
                onValueChange={(v) => {
                  const variation = selectedTest?.variations?.find(vr => vr.id === v);
                  setMetricsForm({
                    ...metricsForm,
                    variation_id: v,
                    impressions: variation?.impressions || 0,
                    clicks: variation?.clicks || 0,
                    conversions: variation?.conversions || 0,
                    revenue: variation?.revenue || 0
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select variation" />
                </SelectTrigger>
                <SelectContent>
                  {selectedTest?.variations?.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.label} {v.is_control && '(Control)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Impressions</Label>
                <Input
                  type="number"
                  value={metricsForm.impressions}
                  onChange={(e) => setMetricsForm({ ...metricsForm, impressions: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Clicks</Label>
                <Input
                  type="number"
                  value={metricsForm.clicks}
                  onChange={(e) => setMetricsForm({ ...metricsForm, clicks: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Conversions</Label>
                <Input
                  type="number"
                  value={metricsForm.conversions}
                  onChange={(e) => setMetricsForm({ ...metricsForm, conversions: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Revenue ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={metricsForm.revenue}
                  onChange={(e) => setMetricsForm({ ...metricsForm, revenue: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Preview calculated metrics */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="text-center">
                <p className="text-lg font-bold text-blue-600">
                  {metricsForm.impressions > 0
                    ? ((metricsForm.clicks / metricsForm.impressions) * 100).toFixed(2)
                    : 0}%
                </p>
                <p className="text-xs text-muted-foreground">CTR</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-green-600">
                  {metricsForm.clicks > 0
                    ? ((metricsForm.conversions / metricsForm.clicks) * 100).toFixed(2)
                    : 0}%
                </p>
                <p className="text-xs text-muted-foreground">Conv. Rate</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMetricsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMetrics} disabled={saving || !metricsForm.variation_id}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Metrics
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Test Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit A/B Test
            </DialogTitle>
            <DialogDescription>
              {selectedTest?.status === 'draft' 
                ? 'Edit all test settings including variations'
                : 'Some fields are locked for active/paused tests'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Test Name *</Label>
                <Input
                  placeholder="e.g., Q1 Campaign Headlines"
                  value={editTest.name}
                  onChange={(e) => setEditTest({ ...editTest, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select
                  value={editTest.content_type}
                  onValueChange={(v) => setEditTest({ ...editTest, content_type: v })}
                  disabled={selectedTest?.status !== 'draft'}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="What are you testing and why?"
                value={editTest.description}
                onChange={(e) => setEditTest({ ...editTest, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Variations - only editable in draft */}
            {selectedTest?.status === 'draft' ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Variations *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addEditVariation}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Variation
                  </Button>
                </div>
                {editTest.variations.map((variation, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <Input
                          value={variation.label}
                          onChange={(e) => updateEditVariation(idx, 'label', e.target.value)}
                          placeholder="Label"
                          className="w-32"
                        />
                        {variation.is_control && (
                          <Badge variant="secondary">Control</Badge>
                        )}
                      </div>
                      <Textarea
                        value={variation.content}
                        onChange={(e) => updateEditVariation(idx, 'content', e.target.value)}
                        placeholder={`Enter ${editTest.content_type} content...`}
                        rows={2}
                      />
                    </div>
                    {editTest.variations.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeEditVariation(idx)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Variations cannot be edited while test is {selectedTest?.status}
                </p>
                <div className="mt-2 space-y-1">
                  {editTest.variations.map((v, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="font-medium">{v.label}:</span> {v.content}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Metric</Label>
                <Select
                  value={editTest.target_metric}
                  onValueChange={(v) => setEditTest({ ...editTest, target_metric: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ctr">Click-Through Rate</SelectItem>
                    <SelectItem value="conversion_rate">Conversion Rate</SelectItem>
                    <SelectItem value="revenue">Revenue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Min. Sample Size</Label>
                <Input
                  type="number"
                  value={editTest.minimum_sample_size}
                  onChange={(e) => setEditTest({ ...editTest, minimum_sample_size: parseInt(e.target.value) || 100 })}
                  min={10}
                />
              </div>
            </div>

            {/* Auto-Optimization Settings */}
            <div className="p-4 bg-amber-50 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-amber-600" />
                  <div>
                    <Label>Auto-Optimization</Label>
                    <p className="text-xs text-muted-foreground">Automatically optimize test performance</p>
                  </div>
                </div>
                <Switch
                  checked={editTest.auto_optimize}
                  onCheckedChange={(v) => setEditTest({ ...editTest, auto_optimize: v })}
                />
              </div>

              {editTest.auto_optimize && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-amber-200">
                  <div className="space-y-2">
                    <Label className="text-xs">Confidence Threshold (%)</Label>
                    <Input
                      type="number"
                      value={editTest.optimization_threshold}
                      onChange={(e) => setEditTest({ ...editTest, optimization_threshold: parseInt(e.target.value) || 90 })}
                      min={50}
                      max={99}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Min Impressions Before Optimize</Label>
                    <Input
                      type="number"
                      value={editTest.min_impressions_before_optimize}
                      onChange={(e) => setEditTest({ ...editTest, min_impressions_before_optimize: parseInt(e.target.value) || 50 })}
                      min={10}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTest} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ABTestingPage;
